import { useState, useMemo } from "react";
import { Dialog, DialogContent, DialogTitle, DialogClose } from "@/components/ui/dialog";
import { Plus, Search, X, Square, CheckSquare } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { allWidgets } from "@/widgets/registry";
import type { WidgetPlacement } from "@/types/widget";
import { placementKey } from "@/types/widget";

export function AddWidgetModal({
  open,
  onOpenChange,
  placements: _placements,
  onAdd,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  placements: WidgetPlacement[];
  onAdd: (widgetId: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<string[]>([]);

  function handleClose() {
    setQuery("");
    setSelected([]);
    onOpenChange(false);
  }

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return allWidgets.filter(
      ({ meta }) =>
        meta.title.toLowerCase().includes(q) ||
        (meta.description ?? "").toLowerCase().includes(q) ||
        meta.category.toLowerCase().includes(q),
    );
  }, [query]);

  function toggleWidget(widgetId: string) {
    setSelected((prev) =>
      prev.includes(widgetId) ? prev.filter((id) => id !== widgetId) : [...prev, widgetId],
    );
  }

  function handleImmediateAdd(widgetId: string) {
    onAdd(widgetId);
    handleClose();
  }

  function handleConfirm() {
    for (const widgetId of selected) {
      onAdd(widgetId);
    }
    handleClose();
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        if (!v) handleClose();
        else onOpenChange(true);
      }}
    >
      <DialogContent
        noClose
        className="max-w-lg p-0 flex flex-col max-h-[80vh] overflow-hidden"
        aria-describedby="add-widget-desc"
      >
        {/* Header */}
        <div className="flex items-center gap-3 px-5 pt-5 pb-4 border-b border-border/60 shrink-0">
          <div className="flex-1 min-w-0">
            <DialogTitle className="text-base font-semibold text-primary">Add Widget</DialogTitle>
            <p id="add-widget-desc" className="text-xs text-muted">
              Select one or more widgets to add to the dashboard.
            </p>
          </div>
          <DialogClose asChild>
            <button
              className="p-1.5 rounded-md text-muted hover:text-primary hover:bg-surface-elevated transition-colors"
              aria-label="Close"
            >
              <X className="h-4 w-4" />
            </button>
          </DialogClose>
        </div>

        {/* Search */}
        <div className="px-4 py-3 shrink-0">
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-surface-elevated border border-border/50">
            <Search className="h-3.5 w-3.5 text-muted shrink-0" />
            <input
              className="flex-1 bg-transparent text-sm text-primary placeholder:text-muted outline-none"
              placeholder="Search widgets…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
            />
            {query && (
              <button onClick={() => setQuery("")} className="text-muted hover:text-primary">
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>
        </div>

        {/* Widget list */}
        <div className="flex-1 overflow-y-auto px-4 pb-4 flex flex-col gap-1">
          {filtered.length === 0 ? (
            <p className="text-center text-sm text-muted py-8">No widgets match your search.</p>
          ) : (
            filtered.map(({ meta }) => {
              const isSelected = selected.includes(meta.id);
              const key = placementKey({
                widgetId: meta.id,
                layout: { i: meta.id, x: 0, y: 0, w: 1, h: 1 },
              });
              return (
                <div
                  key={key}
                  className={cn(
                    "flex items-center gap-2 w-full px-2 py-2 rounded-lg transition-colors group",
                    isSelected
                      ? "bg-brand-cyan/10 ring-1 ring-brand-cyan/40"
                      : "hover:bg-surface-elevated",
                  )}
                >
                  {/* Checkbox — select for batch add */}
                  <button
                    onClick={() => toggleWidget(meta.id)}
                    className="shrink-0 p-0.5 text-muted hover:text-brand-cyan transition-colors"
                    aria-label={isSelected ? "Deselect" : "Select"}
                  >
                    {isSelected ? (
                      <CheckSquare className="h-4 w-4 text-brand-cyan" />
                    ) : (
                      <Square className="h-4 w-4" />
                    )}
                  </button>

                  {/* Row body — click to add immediately */}
                  <button
                    onClick={() => handleImmediateAdd(meta.id)}
                    className="flex items-center gap-3 flex-1 min-w-0 text-left"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-primary truncate">{meta.title}</p>
                      {meta.description && (
                        <p className="text-xs text-muted truncate">{meta.description}</p>
                      )}
                    </div>
                    <Plus className="h-3.5 w-3.5 text-muted group-hover:text-brand-cyan transition-colors shrink-0" />
                  </button>
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2 px-5 py-3 border-t border-border/60 shrink-0">
          <Button variant="outline" size="sm" onClick={handleClose}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleConfirm} disabled={selected.length === 0}>
            {selected.length > 1 ? `Add ${selected.length} widgets` : "Add widget"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
